import React from "react";
import { Link, useLocation } from "react-router-dom";
import { legalTr, legalEn, LegalContent, LEGAL_SLUGS } from "../../content/legal";
import Seo from "../Seo";
import styles from "../kvkkPage.module.css";

interface ILegalPageProps {
  /** Which document to render. */
  docKey: keyof LegalContent;
}

/**
 * Renders one of the compliance/legal documents (Distance Sales, Delivery & Return, Privacy) from
 * src/content/legal.ts. Reachable and linked from the footer (required for İyzico activation) and
 * crawlable like every public page: prerendered (package.json reactSnap.include), listed in
 * public/sitemap.xml and given a full head (canonical, hreflang, description) via Seo. Nothing on
 * shifttracker.net may say noindex (see src/seo/noNoindex.test.ts).
 *
 * The language comes from the URL (Turkish at the root, English under /en/), not from the stored app
 * language, so the prerendered HTML, the hydrated page and the hreflang/canonical always agree.
 */
const LegalPage: React.FC<ILegalPageProps> = ({ docKey }) => {
  const location = useLocation();
  const isEnglish = location.pathname === "/en" || location.pathname.startsWith("/en/");
  const prefix = isEnglish ? "/en" : "";
  const doc = (isEnglish ? legalEn : legalTr)[docKey];
  const { slug, enSlug } = LEGAL_SLUGS[docKey];

  return (
    <div className={styles.page}>
      <Seo
        path={`/${slug}`}
        enPath={`/${enSlug}`}
        title={doc.metaTitle}
        description={doc.metaDescription}
      />
      <div className={styles.container}>
        <h1 className={styles.title}>{doc.title}</h1>
        <p className={styles.updated}>{doc.updated}</p>
        <p className={styles.intro}>{doc.intro}</p>

        {doc.sections.map((section, index) => (
          <section key={index} className={styles.section}>
            <h2 className={styles.heading}>{section.heading}</h2>
            {section.body.map((paragraph, pIndex) => (
              <p key={pIndex} className={styles.text}>
                {paragraph}
              </p>
            ))}
          </section>
        ))}

        <Link className={styles.back} to={`${prefix}/`}>
          {doc.backHome}
        </Link>
      </div>
    </div>
  );
};

export default LegalPage;
